import { formatCurrency } from "@/lib/currency";
import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { CheckCircle2, PackageSearch, Truck, MapPin, PoundSterling, CircleCheckBig } from "lucide-react";
import { Skeleton } from "@/components/ui/skeleton";
import { KpiCard } from "@/components/ui/kpi-card";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Badge } from "@/components/ui/badge";
import { format } from "date-fns";
import { useUserRole } from "@/hooks/useUserRole";
import { useCompany } from "@/hooks/useCompany";
import { cn } from "@/lib/utils";

interface StatsData {
  jobsToday: number;
  awaitingCollection: number;
  awaitingDelivery: number;
  deliveredToday: number;
  driversActive: number;
  revenueToday: number;
}

const JOBS_TODAY_STATUSES = ["pending", "in_progress", "awaiting_delivery", "completed"];
const DELIVERY_STAGE_STATUSES = ["in_progress", "awaiting_delivery"];

const toLocalDateString = (d: Date) => {
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

const getLocalDateBounds = () => {
  const now = new Date();
  const tomorrowDate = new Date(now);
  tomorrowDate.setDate(now.getDate() + 1);

  return {
    today: toLocalDateString(now),
    tomorrow: toLocalDateString(tomorrowDate),
  };
};

const safeFormatDate = (value: string | null | undefined, pattern: string, fallback = "—") => {
  if (!value) return fallback;
  try {
    const parsed = new Date(value);
    if (Number.isNaN(parsed.getTime())) return fallback;
    return format(parsed, pattern);
  } catch {
    return fallback;
  }
};

// "Today" = jobs actually relevant to today's operations.
// Intentionally excludes created_at — a job created today but scheduled for
// tomorrow should not inflate today's counts.
const buildTodayFilter = (today: string, tomorrow: string) =>
  [
    `and(scheduled_date.gte.${today},scheduled_date.lt.${tomorrow})`,
    `and(collection_completed_at.gte.${today},collection_completed_at.lt.${tomorrow})`,
    `and(delivery_completed_at.gte.${today},delivery_completed_at.lt.${tomorrow})`,
    `and(completed_at.gte.${today},completed_at.lt.${tomorrow})`,
  ].join(",");

async function fetchDashboardStats(companyId: string): Promise<StatsData> {
  const { today, tomorrow } = getLocalDateBounds();

  const todayFilter = buildTodayFilter(today, tomorrow);

  const [awaitingCollectionRes, awaitingDeliveryRes, deliveredTodayRes, driversActiveRes, revenueTodayRes] =
    await Promise.all([
      // Awaiting collection = pending jobs active today (scheduled, created, or collected today)
      supabase
        .from("jobs")
        .select("id", { count: "exact", head: true })
        .eq("company_id", companyId)
        .eq("status", "pending")
        .eq("is_draft", false)
        .or(todayFilter),
      // Awaiting delivery = in_progress/awaiting_delivery jobs active today
      supabase
        .from("jobs")
        .select("id", { count: "exact", head: true })
        .eq("company_id", companyId)
        .in("status", DELIVERY_STAGE_STATUSES)
        .eq("is_draft", false)
        .or(todayFilter),
      // Delivered today = jobs with status "completed" and completed_at today
      supabase
        .from("jobs")
        .select("id", { count: "exact", head: true })
        .eq("company_id", companyId)
        .eq("status", "completed")
        .gte("completed_at", today)
        .lt("completed_at", tomorrow)
        .eq("is_draft", false),
      // Drivers active (must have reported location in last 24 hours)
      supabase
        .from("drivers")
        .select("id", { count: "exact", head: true })
        .eq("company_id", companyId)
        .eq("is_active", true)
        .in("status", ["on_job", "available"])
        .gte("last_location_update", new Date(Date.now() - 24 * 60 * 60 * 1000).toISOString()),
      // Revenue today = any active/completed job with activity today
      supabase
        .from("jobs")
        .select("id, estimated_cost, invoice_amount")
        .eq("company_id", companyId)
        .eq("is_draft", false)
        .in("status", JOBS_TODAY_STATUSES)
        .or(todayFilter),
    ]);

  const revenue = (revenueTodayRes.data || []).reduce((sum, job) => {
    return sum + (job.invoice_amount ?? job.estimated_cost ?? 0);
  }, 0);

  const awaitingCollection = awaitingCollectionRes.count ?? 0;
  const awaitingDelivery = awaitingDeliveryRes.count ?? 0;
  const deliveredToday = deliveredTodayRes.count ?? 0;

  return {
    jobsToday: awaitingCollection + awaitingDelivery + deliveredToday,
    awaitingCollection,
    awaitingDelivery,
    deliveredToday,
    driversActive: driversActiveRes.count ?? 0,
    revenueToday: revenue,
  };
}

type StatKey = keyof StatsData;

interface DetailJob {
  id: string;
  vehicle_registration: string;
  vehicle_make: string;
  vehicle_model: string;
  status: string;
  scheduled_date: string | null;
  delivery_address: string;
  delivery_contact: string;
  estimated_cost: number | null;
  invoice_amount: number | null;
}

interface DetailDriver {
  id: string;
  name: string;
  phone: string;
  status: string;
  last_location_update: string | null;
}

async function fetchDetailData(key: StatKey, companyId: string) {
  const { today, tomorrow } = getLocalDateBounds();
  const todayFilter = buildTodayFilter(today, tomorrow);

  const jobFields = "id, vehicle_registration, vehicle_make, vehicle_model, status, scheduled_date, delivery_address, delivery_contact, estimated_cost, invoice_amount";

  switch (key) {
    case "jobsToday": {
      const [activeJobsRes, deliveredJobsRes] = await Promise.all([
        supabase
          .from("jobs")
          .select(jobFields)
          .eq("company_id", companyId)
          .in("status", ["pending", ...DELIVERY_STAGE_STATUSES])
          .eq("is_draft", false)
          .or(todayFilter)
          .limit(100),
        supabase
          .from("jobs")
          .select(jobFields)
          .eq("company_id", companyId)
          .eq("status", "completed")
          .gte("completed_at", today)
          .lt("completed_at", tomorrow)
          .eq("is_draft", false)
          .limit(100),
      ]);

      const merged = [...(activeJobsRes.data || []), ...(deliveredJobsRes.data || [])];
      const uniqueById = Array.from(new Map(merged.map((job) => [job.id, job])).values());

      return { type: "jobs" as const, data: uniqueById as DetailJob[] };
    }
    case "awaitingCollection": {
      const { data } = await supabase
        .from("jobs")
        .select(jobFields)
        .eq("company_id", companyId)
        .eq("status", "pending")
        .eq("is_draft", false)
        .or(todayFilter)
        .order("scheduled_date", { ascending: true })
        .limit(50);
      return { type: "jobs" as const, data: (data || []) as DetailJob[] };
    }
    case "awaitingDelivery": {
      const { data } = await supabase
        .from("jobs")
        .select(jobFields)
        .eq("company_id", companyId)
        .in("status", DELIVERY_STAGE_STATUSES)
        .eq("is_draft", false)
        .or(todayFilter)
        .order("scheduled_date", { ascending: true })
        .limit(50);
      return { type: "jobs" as const, data: (data || []) as DetailJob[] };
    }
    case "deliveredToday": {
      const { data } = await supabase
        .from("jobs")
        .select(jobFields)
        .eq("company_id", companyId)
        .eq("status", "completed")
        .gte("completed_at", today)
        .lt("completed_at", tomorrow)
        .eq("is_draft", false)
        .order("completed_at", { ascending: false })
        .limit(50);
      return { type: "jobs" as const, data: (data || []) as DetailJob[] };
    }
    case "driversActive": {
      const { data } = await supabase
        .from("drivers")
        .select("id, name, phone, status, last_location_update")
        .eq("company_id", companyId)
        .eq("is_active", true)
        .in("status", ["on_job", "available"])
        .gte("last_location_update", new Date(Date.now() - 24 * 60 * 60 * 1000).toISOString())
        .order("name", { ascending: true })
        .limit(50);
      return { type: "drivers" as const, data: (data || []) as DetailDriver[] };
    }
    case "revenueToday": {
      const { data } = await supabase
        .from("jobs")
        .select(jobFields)
        .eq("company_id", companyId)
        .eq("is_draft", false)
        .in("status", JOBS_TODAY_STATUSES)
        .or(todayFilter)
        .limit(100);
      return { type: "jobs" as const, data: (data || []) as DetailJob[] };
    }
  }
}

interface StatCardConfig {
  key: StatKey;
  title: string;
  icon: React.ComponentType<{ className?: string }>;
  adminOnly?: boolean;
  formatValue?: (val: number) => string;
}

const statCards: StatCardConfig[] = [
  { key: "jobsToday", title: "Jobs Today", icon: CheckCircle2 },
  { key: "awaitingCollection", title: "Awaiting Collection", icon: PackageSearch },
  { key: "awaitingDelivery", title: "Awaiting Delivery", icon: Truck },
  { key: "deliveredToday", title: "Delivered Today", icon: CircleCheckBig },
  { key: "driversActive", title: "Drivers Active", icon: MapPin },
  {
    key: "revenueToday",
    title: "Revenue Today",
    icon: PoundSterling,
    adminOnly: true,
    formatValue: (val) => `${formatCurrency(val.toFixed(2))}`,
  },
];

const statusColor: Record<string, string> = {
  pending: "bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300",
  in_progress: "bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300",
  awaiting_delivery: "bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-300",
  completed: "bg-emerald-100 text-emerald-800 dark:bg-emerald-900/30 dark:text-emerald-300",
  available: "bg-emerald-100 text-emerald-800 dark:bg-emerald-900/30 dark:text-emerald-300",
  on_job: "bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300",
  offline: "bg-muted text-muted-foreground",
};

export function DashboardStatsStrip() {
  const [openKey, setOpenKey] = useState<StatKey | null>(null);
  const { isAdmin } = useUserRole();
  const { companyId, loading: companyLoading } = useCompany();

  const { data, isLoading } = useQuery({
    queryKey: ["dashboard-stats-strip", companyId],
    queryFn: () => {
      if (!companyId) throw new Error("Active company is not resolved");
      return fetchDashboardStats(companyId);
    },
    enabled: !!companyId && !companyLoading,
    refetchInterval: 30_000,
    refetchOnWindowFocus: true,
  });

  const { data: detailData, isLoading: detailLoading } = useQuery({
    queryKey: ["dashboard-stat-detail", companyId, openKey],
    queryFn: () => {
      if (!openKey || !companyId) throw new Error("Dashboard detail context is not resolved");
      return fetchDetailData(openKey, companyId);
    },
    enabled: !!companyId && !companyLoading && !!openKey,
  });

  const visibleCards = statCards.filter((c) => !c.adminOnly || isAdmin);
  const openCard = statCards.find((c) => c.key === openKey);

  return (
    <>
      <div className={cn("grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-3", isAdmin ? "2xl:grid-cols-6" : "2xl:grid-cols-5")}>
        {visibleCards.map((card) => {
          const rawValue = data?.[card.key] ?? 0;
          const displayValue = card.formatValue ? card.formatValue(rawValue) : String(rawValue);

          return (
            <KpiCard
              key={card.key}
              label={card.title}
              value={displayValue}
              icon={card.icon}
              loading={isLoading}
              tone={card.key === "revenueToday" || card.key === "deliveredToday" ? "success" : "default"}
              onClick={() => setOpenKey(card.key)}
            />
          );
        })}
      </div>

      <Dialog open={!!openKey} onOpenChange={(open) => !open && setOpenKey(null)}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              {openCard && <openCard.icon className="h-5 w-5" />}
              {openCard?.title}
              <Badge variant="secondary" className="ml-auto mr-6">
                {openKey && data
                  ? openCard?.formatValue
                    ? openCard.formatValue(data[openKey])
                    : data[openKey]
                  : 0}
              </Badge>
            </DialogTitle>
          </DialogHeader>

          <ScrollArea className="max-h-[400px]">
            {detailLoading ? (
              <div className="space-y-3 p-1">
                {[1, 2, 3].map((i) => (
                  <Skeleton key={i} className="h-16 w-full rounded-lg" />
                ))}
              </div>
            ) : !detailData?.data?.length ? (
              <p className="text-sm text-muted-foreground text-center py-8">
                No records found
              </p>
            ) : detailData.type === "jobs" ? (
              <div className="space-y-2 p-1">
                {(detailData.data as DetailJob[]).map((job) => (
                  <div
                    key={job.id}
                    className="rounded-lg border p-3 space-y-1"
                  >
                    <div className="flex items-center justify-between">
                      <span className="font-semibold text-sm">
                        {job.vehicle_registration}
                      </span>
                      <span
                        className={`text-[10px] px-2 py-0.5 rounded-full font-medium ${statusColor[job.status] || "bg-muted text-muted-foreground"}`}
                      >
                        {job.status.replace(/_/g, " ")}
                      </span>
                    </div>
                    <p className="text-xs text-muted-foreground">
                      {job.vehicle_make} {job.vehicle_model}
                    </p>
                    <p className="text-xs text-muted-foreground break-words">
                      {job.delivery_contact} — {job.delivery_address}
                    </p>
                    <div className="flex items-center justify-between">
                      {job.scheduled_date && (
                        <p className="text-[10px] text-muted-foreground">
                          Scheduled: {safeFormatDate(job.scheduled_date, "dd MMM yyyy, HH:mm")}
                        </p>
                      )}
                      {isAdmin && (job.invoice_amount ?? job.estimated_cost) != null && (
                        <span className="text-xs font-semibold text-emerald-600 dark:text-emerald-400">
                          {formatCurrency(((job.invoice_amount ?? job.estimated_cost) || 0).toFixed(2))}
                        </span>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="space-y-2 p-1">
                {(detailData.data as DetailDriver[]).map((driver) => (
                  <div
                    key={driver.id}
                    className="rounded-lg border p-3 space-y-1"
                  >
                    <div className="flex items-center justify-between">
                      <span className="font-semibold text-sm">{driver.name}</span>
                      <span
                        className={`text-[10px] px-2 py-0.5 rounded-full font-medium ${statusColor[driver.status] || "bg-muted text-muted-foreground"}`}
                      >
                        {driver.status.replace(/_/g, " ")}
                      </span>
                    </div>
                    <p className="text-xs text-muted-foreground">{driver.phone}</p>
                    {driver.last_location_update && (
                      <p className="text-[10px] text-muted-foreground">
                        Last seen: {safeFormatDate(driver.last_location_update, "dd MMM yyyy, HH:mm")}
                      </p>
                    )}
                  </div>
                ))}
              </div>
            )}
          </ScrollArea>
        </DialogContent>
      </Dialog>
    </>
  );
}
